/**
 * @module Core
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { HttpHeaders } from '@angular/common/http';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { Md5 } from 'ts-md5/dist/md5';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { SETTINGS } from '@environment/settings';

// @TODO is this public outside of Core?
/**
 * Common header builder
 */
export abstract class CommonHeadersBuilder {
  protected headers: HttpHeaders;
  protected stateStoreUtils: StateStoreUtilsService;
  protected commonUtil: CommonUtilService;
  protected channel: string;

  constructor(stateStore: StateStoreUtilsService, commonUtilService: CommonUtilService) {
    this.stateStoreUtils = stateStore;
    this.commonUtil = commonUtilService;
  }

  /**
   * Adds common headers
   * @param headers
   */
  public addCommonHeaders(headers: HttpHeaders): HttpHeaders {
    if (headers === null || headers === undefined) {
      headers = new HttpHeaders();
    }
    this.channel = SETTINGS.CHANNEL.toLocaleLowerCase();
    const applicationId = this.channel === 'nrd' ? 'RAP' : 'REBELLION';
    return headers
      .append('Content-type', 'application/json')
      .append('senderId', 'TMO')
      .append('applicationId', applicationId)
      .append('tl_referer', window.location.href);
  }

  /**
   * Method to add interaction id to header
   */
  public addInteractionId(): void {
    let interactionId = this.stateStoreUtils.getInteractionId();
    const limit: number = 49;
    // E2E Fix: PR73155 - Changed format for interaction id
    const limitForSessionId: number = 20;
    const limitForDate: number = 5;
    let dateForInteractionId = new Date().getTime().toString();
    dateForInteractionId = dateForInteractionId = dateForInteractionId.split('').reverse().join('');
    if (!interactionId) {
      this.channel = SETTINGS.CHANNEL.toLocaleLowerCase();
      const token = this.getTokenId();
      const sessionId = Md5.hashStr(token);
      const dynamicSessionSubString = this.stateStoreUtils.getBscsCustomerID() || 'ANON';
      // PR75167 - Updated code to make changes to interaction id generation for web channel
      if (this.channel !== 'web') {
        const interactionIdSubString = this.stateStoreUtils.getApplicationUserId()
          ? this.stateStoreUtils.getApplicationUserId()
          : '';
        const dynamicSessionID = ''.concat(dynamicSessionSubString, dateForInteractionId);
        interactionId = this.trimId(
          this.channel.concat(
            interactionIdSubString,
            this.trimId(sessionId.toString(), limitForSessionId),
            dynamicSessionID
          ),
          limit
        );
      } else {
        const interactionIdSubString = '';
        const dynamicSessionID = ''.concat(
          dynamicSessionSubString,
          this.trimId(dateForInteractionId.toString(), limitForDate)
        );
        interactionId = this.trimId(
          this.channel.concat(interactionIdSubString, dynamicSessionID, sessionId.toString()),
          limit
        );
      }
    }

    this.headers = this.headers.append('interactionId', interactionId);
    this.stateStoreUtils.setInteractionId(interactionId);
  }

  /**
   * Adds session Id to header and stateStoreUtils
   */
  public addSessionId(): void {
    let sessionId: string;
    const tokenId = this.getTokenId();

    sessionId = tokenId ? Md5.hashStr(tokenId).toString() : 'NA';
    this.stateStoreUtils.setSessionId(sessionId);
    this.headers = this.headers.append('sessionId', sessionId);
  }

  /**
   * Gets the token id from localStorage/stateStoreUtils
   */
  public getTokenId(): string {
    return this.stateStoreUtils.getAuthToken()
      ? this.stateStoreUtils.getAuthToken()
      : this.stateStoreUtils.getToken();
  }

  /**
   * Add activity id to header using customer id / random number
   */
  public addActivityId(): void {
    let generatedId: string;
    let activityId: string;
    const limit: number = 32;
    const randomNumber: number = 20;
    const timestamp = new Date().getTime();

    const customerId = this.stateStoreUtils.getCustomerId();
    if (this.commonUtil.isNotEmpty(customerId)) {
      generatedId = ''.concat(
        timestamp.toString(),
        customerId,
        this.commonUtil.getRandom(randomNumber).toString()
      );
    } else {
      generatedId = ''.concat(
        timestamp.toString(),
        this.commonUtil.getRandom(randomNumber).toString(),
        this.commonUtil.getRandom(randomNumber).toString()
      );
    }

    activityId = this.trimId(generatedId, limit);
    this.headers = this.headers.append('activityId', activityId.toString());
  }

  /**
   * Trims string based on the trim limit
   * @param id: string to be trimmed
   * @param limit: limit
   */
  public trimId(id: string, limit: number): string {
    return id.length > limit ? id.substring(0, limit) : id;
  }

  /**
   * Gets headers
   */
  public getHeaders(): HttpHeaders {
    // retrieve interactionId from sessionStorage and store it in stateStoreUtils
      this.headers = this.headers.set('interactionId', sessionStorage.getItem('interactionId').toString());
      this.stateStoreUtils.setInteractionId(sessionStorage.getItem('interactionId').toString());
    return this.headers;
  }

  /**
   * Add store id to header
   */
  public addStoreHeaders(): void {
    const storeId = this.stateStoreUtils.getStoreId();
    if (SETTINGS.CHANNEL.toLocaleLowerCase() === 'retail' && this.commonUtil.isNotEmpty(storeId)) {
      this.headers = this.headers.append('storeId', storeId);
      const terminalId: string = this.stateStoreUtils.getTerminalData()
        ? this.stateStoreUtils.getTerminalData().id
        : '';
      this.headers = this.headers
        .append('terminalId', terminalId)
        .append('tillId', this.stateStoreUtils.getposTillId() || '');
    }
  }

  public addWorkflowId(): void {
    const workflowId = this.stateStoreUtils.getWorkflowId() || 'NA';
    this.headers = this.headers.append('workflowId', workflowId);
  }
  /**
   * Add screen id to header
   */
  public addScreenId(): void {
    const permissionObject = this.stateStoreUtils.getAppState();
    const screenId = this.commonUtil.getScreenId(
      permissionObject,
      this.stateStoreUtils.getScreenId()
    );
    this.headers = this.headers.append('screenId', screenId);
  }
  public abstract addApplicationUserId(): void;

  public abstract addChannel(): void;

  public abstract addAuthorization(): void;
}
